import React from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import NavigationIcon from "@mui/icons-material/Navigation";
import PhotoSizeSelectSmallIcon from "@mui/icons-material/PhotoSizeSelectSmall";
import RoomIcon from "@mui/icons-material/Room";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import RswState, { getRswStateDescription } from "../../enums/robot/rsw/rsw-state";
import SchedulingStateType, { getSchedulingStateTypeDescription } from "../../enums/robot/rsw/scheduling-state-type";
import { getNavigatorDescriptionOrNull } from "../../interfaces/robot/navigator";
import { convertSecondsToHourMinSecString, getDateTimeFormatted } from "../../utils/date";
import RobotIcon from "../../utils/icons/RobotIcon";
import ClickableBox from "../General/ClickableBox";
import SubsectionsCard from "../General/SubsectionsCard";
import BatteryIcon from "./BatteryIcon";
import SchedulingIcon from "./SchedulingIcon";
import SkeletonizedDashboardText from "./SkeletonizedDashboardText";
import StatusLight, { getRswStateStatusLightColor } from "./StatusLight";
import ZoneIconActionIconImg from "../../images/dashboard-icons/zoneIconAction.svg";
import Grid from "@mui/material/Grid";
import { NavigatorName } from "../../enums/command-hub/navigator";
import { selectCurrentRobotState } from "../../store/status/status-selectors";
import { useSharedSelector } from "../../store/SharedProvider";
import {
  selectAllCleaningZonesIdName,
  selectCurrentRobotCurrentRoom,
  selectCurrentRobotMissions,
} from "../../store/room/room-selectors";
import { selectIsWebApp } from "../../store/app-configurations/app-configurations-selectors";
import DistanceUnitSet from "../misc/DistanceUnitSet";
import { DistanceValueUnit } from "../../enums/configuration/units";

const DashboardStatusInfo: React.FC<{ onRoomClick: (roomId: string) => void; hideScheduling?: boolean }> = ({
  onRoomClick,
  hideScheduling = false,
}) => {
  const { t } = useTranslation();

  const robotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const currentRoom = useSharedSelector((state) => selectCurrentRobotCurrentRoom(state));
  const missions = useSharedSelector((state) => selectCurrentRobotMissions(state));
  const cleaningZoneNames = useSharedSelector((state) => selectAllCleaningZonesIdName(state));
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const mission = missions?.find((m) => m.id === robotState?.mission?.activeMissionId);
  const currentNavigator = mission?.mode ?? robotState?.cleaningData?.currentNavigator;

  const isCleaning =
    currentNavigator !== null &&
    currentNavigator !== "" &&
    robotState?.status?.state != null &&
    ![RswState.Waiting, RswState.Homing].includes(robotState.status.state);

  const cleaningStartDate =
    robotState?.cleaningData?.cleaningStart != null ? new Date(robotState.cleaningData.cleaningStart) : null;
  const cleaningEndDate =
    robotState?.cleaningData?.cleaningEnd != null ? new Date(robotState.cleaningData.cleaningEnd) : null;

  const formatCleaningDuration = () => {
    if (cleaningStartDate == null || cleaningEndDate == null) {
      return null;
    }

    let cleaningDurationSec = (cleaningEndDate.valueOf() - cleaningStartDate.valueOf()) / 1000;
    return convertSecondsToHourMinSecString(cleaningDurationSec);
  };

  const formatSchedulingTime = () => {
    if (robotState?.scheduling?.start == null || robotState?.scheduling?.end == null) {
      return null;
    }

    const startDateTime = DateTime.fromJSDate(new Date(robotState.scheduling.start));
    const endDateTime = DateTime.fromJSDate(new Date(robotState.scheduling.end));

    // \u00A0 instead of " " to prevent line breaks in time strings
    const startStr = getDateTimeFormatted(startDateTime, "ccc, HH:mm").replace(" ", "\u00A0");
    const isCurrentSchedule = DateTime.now() >= startDateTime && DateTime.now() <= endDateTime;

    return `${isCurrentSchedule ? t("current-schedule") : t("next-schedule")}: ${startStr}`;
  };

  const formatCleaningPerformance = () => {
    if (robotState?.cleaningData?.cleanedArea == null || cleaningStartDate == null || cleaningEndDate == null) {
      return null;
    }

    const cleaningDurationHoursDecimal = (cleaningEndDate.valueOf() - cleaningStartDate.valueOf()) / (1000 * 60 * 60);
    const cleaningPerformanceM2h =
      cleaningDurationHoursDecimal === 0
        ? 0
        : Math.round(robotState.cleaningData.cleanedArea / cleaningDurationHoursDecimal);
    return <DistanceUnitSet value={cleaningPerformanceM2h} valueUnit={DistanceValueUnit.METERS_SQUARED_PER_HOUR} />;
  };

  const roomClickHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();

    if (currentRoom?.id != null) {
      onRoomClick(currentRoom.id);
    }
  };

  return (
    <SubsectionsCard
      title={t("dashboard-status")}
      disableCardGutters
      content={[
        {
          title: (
            <Stack direction="row" alignItems="center" spacing={1} py={1}>
              <RobotIcon />
              <Typography>{t("dashboard-robot-status")}</Typography>
            </Stack>
          ),
          content: (
            <Grid container>
              <Grid item xs={12} md={4} data-testid='robot-dashboard-robot-state-description'>
                <SkeletonizedDashboardText
                  content={getRswStateDescription(robotState?.status?.state)}
                  icon={<StatusLight color={getRswStateStatusLightColor(robotState?.status?.state ?? null)} />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SkeletonizedDashboardText
                  content={
                    robotState?.status?.batteryPercentage != null ? `${robotState.status.batteryPercentage}%` : null
                  }
                  icon={
                    <BatteryIcon
                      batteryPercentage={robotState?.status?.batteryPercentage}
                      isCharging={robotState?.status?.isCharging}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ClickableBox
                  sx={{ py: 0.5, borderRadius: 1, width: "100%" }}
                  onClick={roomClickHandler}
                  tooltipText={t("button-open-room")}
                  disabled={!robotState?.status?.roomId || !currentRoom}
                >
                  <SkeletonizedDashboardText
                    content={currentRoom?.name}
                    hideIfNoContent={robotState?.status != null}
                    icon={<RoomIcon color="action" />}
                  />
                </ClickableBox>
              </Grid>
            </Grid>
          ),
        },

        isWebapp
          ? {
              title: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <CleaningServicesIcon />
                  <Typography>{t("dashboard-cleaning-status")}</Typography>
                </Stack>
              ),

              content: (
                <Grid container>
                  {!isCleaning && (
                    <Grid item xs={12} md={4}>
                      <SkeletonizedDashboardText
                        content={robotState?.status != null ? t("robot-not-cleaning") : null}
                        icon={<InfoOutlineIcon color="action" />}
                      />
                    </Grid>
                  )}
                  {isCleaning && (
                    <React.Fragment>
                      <Grid item xs={12} md={4}>
                        <SkeletonizedDashboardText
                          content={getNavigatorDescriptionOrNull(currentNavigator ?? null)}
                          icon={<NavigationIcon color="action" />}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          // needed to avoid layout shift
                          display: { xs: mission == null ? "none" : "block", md: "block" },
                        }}
                      >
                        <SkeletonizedDashboardText
                          content={mission?.name}
                          hideIfNoContent
                          icon={<AssignmentOutlinedIcon sx={{ color: mission?.color }} />}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          // needed to avoid layout shift
                          display: {
                            xs: currentNavigator !== NavigatorName.ZoneCleaning ? "none" : "block",
                            md: "block",
                          },
                        }}
                      >
                        <SkeletonizedDashboardText
                          content={
                            cleaningZoneNames?.find((z) => z.id === robotState.cleaningData?.currentZoneId)?.name
                          }
                          hideIfNoContent={currentNavigator !== NavigatorName.ZoneCleaning}
                          icon={
                            <Icon sx={{ display: "flex", alignItems: "center" }}>
                              <img src={ZoneIconActionIconImg} alt="ZoneCleaning" />
                            </Icon>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <SkeletonizedDashboardText
                          content={formatCleaningDuration()}
                          icon={<TimerIcon color="action" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SkeletonizedDashboardText
                          content={
                            robotState.cleaningData?.cleanedArea == null ? null : (
                              <DistanceUnitSet
                                value={robotState.cleaningData.cleanedArea}
                                valueUnit={DistanceValueUnit.METERS_SQUARED}
                              />
                            )
                          }
                          icon={<PhotoSizeSelectSmallIcon color="action" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SkeletonizedDashboardText
                          content={formatCleaningPerformance()}
                          icon={<SpeedIcon color="action" />}
                        />
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              ),
            }
          : null,

        !hideScheduling
          ? {
              title: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <TodayRoundedIcon />
                  <Typography>{t("dashboard-scheduling-status")}</Typography>
                </Stack>
              ),
              content: (
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <SkeletonizedDashboardText
                      content={getSchedulingStateTypeDescription(robotState?.scheduling?.type)}
                      icon={<SchedulingIcon schedulingStateType={robotState?.scheduling?.type} altColor="action" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SkeletonizedDashboardText
                      content={formatSchedulingTime()}
                      icon={<AccessTimeIcon color="action" />}
                      hideIfNoContent={
                        robotState?.scheduling?.type != null &&
                        ![SchedulingStateType.Active, SchedulingStateType.Pending].includes(
                          robotState?.scheduling?.type
                        )
                      }
                    />
                  </Grid>
                </Grid>
              ),
            }
          : null,
      ]}
    />
  );
};

export default DashboardStatusInfo;
